import { useEffect, useState } from 'react';
import { ApiError, match, type MatchInput, type MatchResult } from '../lib/api.js';
import { useFlywheel } from '../lib/flywheel.js';

interface Props {
  input: MatchInput;
  onPick: (m: MatchResult) => void;
  onBack: () => void;
}

/**
 * Top-3 ranked T3 personas with LLM-generated "why this fits" reasons.
 * Synthetic agents carry a 仮想キャラクター badge per CLAUDE.md §5.2 and
 * Decision 14 — the label must be visible on the match card, so this
 * component is one of the three places it appears.
 */
export function MatchResults({ input, onPick, onBack }: Props) {
  const [matches, setMatches] = useState<MatchResult[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { emit } = useFlywheel();

  useEffect(() => {
    let cancelled = false;
    match(input)
      .then((r) => {
        if (cancelled) return;
        setMatches(r.matches);
        // Candidate searched and got matches → signal flows to operator,
        // who'd see "candidates are looking for X" in the dashboard.
        emit({
          kind: 'MATCH_CREATED',
          actor: 'candidate',
          flowsTo: 'operator',
          note: `候補者が${r.matches.length}名の先輩候補を探した`,
        });
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof ApiError ? `(${err.status}) ${err.message}` : String(err));
      });
    return () => {
      cancelled = true;
    };
  }, [input, emit]);

  return (
    <div className="main">
      <div className="container">
        <h1>合いそうな先輩を3名挙げました。</h1>
        <p className="lede">
          話してみたい先輩を選んでください。途中で別の先輩に切り替えても大丈夫です。
        </p>

        {error && <div className="error">うまく取得できませんでした: {error}</div>}

        {!matches && !error && (
          <div className="muted center loading-block">候補を絞っています...</div>
        )}

        {matches && matches.length === 0 && (
          <div className="muted">
            今回の関心と状況だと、ぴったりの先輩がまだ見つかりませんでした。条件を変えてみてください。
          </div>
        )}

        {matches && matches.length > 0 && (
          <div className="match-grid">
            {matches.map((m) => (
              <button
                key={m.agent_id}
                type="button"
                className="match-card"
                onClick={() => onPick(m)}
              >
                <div className="avatar">
                  {m.display_name.slice(0, 1)}
                </div>
                <div>
                  <div className="match-name">
                    <h2>{m.display_name}</h2>
                    {m.type === 'synthetic' && (
                      <span className="virtual-badge">仮想キャラクター</span>
                    )}
                  </div>
                  <p className="match-headline">{m.headline}</p>
                  {m.reason && <p className="match-reason">{m.reason}</p>}
                </div>
                <span className="match-score" title="score 0–1">
                  {(m.score * 100).toFixed(0)}%
                </span>
              </button>
            ))}
          </div>
        )}

        <div className="row row-trailing">
          <button type="button" className="btn-ghost" onClick={onBack}>
            ← 条件を変える
          </button>
        </div>
      </div>
    </div>
  );
}
